/**
 * The vendor console's pure decisions (phase 7b, D15, D27): what the tables
 * show and how states map to badges, table-tested in `vendor.test.ts` so the
 * components only render.
 */
import type { SeverityOpenWire } from '@acp/api-contract/alerts.js';
import type { AnnouncementSeverityWire, RolloutNodeWire, SitePinViewWire, VendorNodeWire, VendorProductionWire } from '@acp/api-contract/vendor.js';
import { isPermitted } from '@acp/domain/access.js';
import type { Grants } from './permissions.js';

/**
 * Whether to show the vendor console, and its write controls. Unlike every
 * other control (`permissions.ts`), UNKNOWN grants (a node, which serves no
 * `/me`) show nothing: the console is the cloud's, and a node has no vendor
 * routes to offer. Vendor permissions are tenant-wide (D27).
 */
export function vendorAccessOf(grants: Grants): { readonly canSee: boolean; readonly canAdmin: boolean } {
  if (grants === null) return { canSee: false, canAdmin: false };
  const tenantWide = { siteId: null, studioId: null };
  const canAdmin = isPermitted(grants, 'vendor.admin', tenantWide);
  return { canSee: canAdmin || isPermitted(grants, 'vendor.ops', tenantWide), canAdmin };
}

/** A certificate under this long from expiry is flagged (PLAN §11.1's alarm: "cert expiring within 14 days"). */
export const CERT_WARNING_MS = 14 * 24 * 60 * 60 * 1000;

/** A clock off by more than this is flagged (PLAN §11.1: "clock drift over 50 ms"). */
export const CLOCK_DRIFT_LIMIT_MS = 50;

/** The broadcast badge variants the vendor screens use. */
export type BadgeTone = 'success' | 'warning' | 'offline' | 'standby' | 'onair' | 'destructive' | 'secondary';

/** A hash for people: its first 12 characters. */
export function shortHash(hash: string | null): string {
  return hash === null ? '–' : hash.slice(0, 12);
}

export type CertState = { readonly tone: BadgeTone; readonly text: string };

/** A node's certificate: none yet, expired, expiring within 14 days, or fine — with the days left. */
export function certState(certExpiresAt: string | null, nowMs: number): CertState {
  if (certExpiresAt === null) return { tone: 'secondary', text: 'none' };
  const left = Date.parse(certExpiresAt) - nowMs;
  const days = Math.floor(left / (24 * 60 * 60 * 1000));
  if (left <= 0) return { tone: 'destructive', text: 'expired' };
  if (left < CERT_WARNING_MS) return { tone: 'warning', text: `${String(days)} d left` };
  return { tone: 'success', text: `${String(days)} d left` };
}

/** Whether a node's clock is off by more than the limit (either way). */
export function driftExceeded(clockDriftMs: number | null): boolean {
  return clockDriftMs !== null && Math.abs(clockDriftMs) > CLOCK_DRIFT_LIMIT_MS;
}

export function linkTone(linkState: VendorNodeWire['linkState']): BadgeTone {
  return linkState === 'connected' ? 'success' : linkState === 'disconnected' ? 'offline' : 'secondary';
}

export function protectionTone(protection: VendorProductionWire['protection']): BadgeTone {
  switch (protection) {
    case 'protected':
      return 'success';
    case 'syncing':
      return 'standby';
    case 'unprotected':
      return 'destructive';
    case 'single':
    case 'unknown':
      return 'secondary';
  }
}

export function alarmTone(severity: SeverityOpenWire): BadgeTone {
  return severity === 'critical' ? 'destructive' : severity === 'warning' ? 'warning' : 'secondary';
}

export function announcementTone(severity: AnnouncementSeverityWire): BadgeTone {
  switch (severity) {
    case 'critical':
    case 'major':
      return 'destructive';
    case 'minor':
      return 'warning';
    case 'info':
      return 'standby';
  }
}

// `unknown`: a severity from a newer cloud (D37), sorted with the warnings.
const ALARM_RANK: Readonly<Record<SeverityOpenWire, number>> = { critical: 0, warning: 1, unknown: 1, info: 2 };

/** Alarms worst first, then by tenant and subject: the dashboard's order. */
export function sortAlarms<T extends { readonly severity: SeverityOpenWire; readonly tenantId: string; readonly subject: string }>(alarms: readonly T[]): T[] {
  return [...alarms].sort((a, b) => ALARM_RANK[a.severity] - ALARM_RANK[b.severity] || a.tenantId.localeCompare(b.tenantId) || a.subject.localeCompare(b.subject));
}

/** "n of m nodes on target", and how many have nothing published to be on. */
export function rolloutSummary(rollout: readonly RolloutNodeWire[]): { readonly onTarget: number; readonly total: number } {
  return { onTarget: rollout.filter((n) => n.upToDate).length, total: rollout.length };
}

/** A pin form's target: the stage, or a tenant's site (an empty id is still to be entered). */
export type PinFormTarget = { readonly kind: 'stage' } | { readonly kind: 'site'; readonly tenantId: string; readonly siteId: string };

const TENANT_ID = /^[a-z][a-z0-9-]{2,39}$/;
const SITE_ID = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

/**
 * The pin a form would submit, trimmed, or null while it is not ready: no release chosen, or a
 * site without a valid tenant id AND site id (site ids are unique only within a tenant).
 */
export function pinOf(target: PinFormTarget, tenantId: string, siteId: string, listHash: string): PinFormTarget | null {
  if (listHash === '') return null;
  if (target.kind === 'stage') return target;
  const site = { kind: 'site', tenantId: tenantId.trim(), siteId: siteId.trim() } as const;
  return TENANT_ID.test(site.tenantId) && SITE_ID.test(site.siteId) ? site : null;
}

/**
 * The tenant through which to clear a legacy site pin (keyed by site id alone): the clear route
 * is a tenant's (`/vendor/tenants/{tenantId}/pins/sites/{siteId}`) and removes that tenant's own
 * pin of the site too, so this is a tenant the view knows — one with a node of that site first —
 * that has NO pin of its own for it. Null when every known tenant has one.
 */
export function legacyClearTenant(releases: { readonly sitePins: readonly SitePinViewWire[]; readonly rollout: readonly RolloutNodeWire[] }, siteId: string): string | null {
  const own = new Set(releases.sitePins.flatMap((p) => (p.tenantId !== null && p.siteId === siteId ? [p.tenantId] : [])));
  const candidates = [
    ...releases.rollout.filter((n) => n.siteId === siteId).map((n) => n.tenantId),
    ...releases.rollout.map((n) => n.tenantId),
    ...releases.sitePins.flatMap((p) => (p.tenantId === null ? [] : [p.tenantId])),
  ];
  return candidates.find((t) => !own.has(t)) ?? null;
}

/** "12 s ago", "5 min ago", "3 h ago", "2 d ago" — or "never". */
export function agoText(iso: string | null, nowMs: number): string {
  if (iso === null) return 'never';
  const s = Math.max(0, Math.round((nowMs - Date.parse(iso)) / 1000));
  if (s < 60) return `${String(s)} s ago`;
  if (s < 3600) return `${String(Math.floor(s / 60))} min ago`;
  if (s < 86_400) return `${String(Math.floor(s / 3600))} h ago`;
  return `${String(Math.floor(s / 86_400))} d ago`;
}

/** Local `datetime-local` input text (minutes) for an ISO instant, and back. Empty text is null. */
export function toLocalInput(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromLocalInput(text: string): string | null {
  if (text.trim() === '') return null;
  const ms = new Date(text).getTime();
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}
